import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Calendar,
  Plus,
  Trash2,
  Save,
  X,
  Users,
  CreditCard,
  Edit3,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import MarkdownHint from '../components/MarkdownHint';
import { Badge } from '../components/ui/badge';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { eventsAPI, locationsAPI } from '../utils/api';
import { toast } from 'sonner';

const blankEvent = () => ({
  name: '',
  start_date: '',
  end_date: '',
  max_persons: 20,
  price_per_person: 0,
  description: '',
  details: '',
  photos: [],
});

/**
 * Browser-side image compression (same approach as AddLocation).
 * Downscales to fit `maxDim`, re-encodes as JPEG. Returns a base64 data-URL.
 * Keeps event payloads small – two photos × ~200 KB stays well under
 * MongoDB's 16 MB BSON limit.
 */
function compressImage(file, maxDim = 1200, quality = 0.72) {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith('image/') || file.type === 'image/svg+xml') {
      const r = new FileReader();
      r.onloadend = () => resolve(r.result);
      r.onerror = reject;
      r.readAsDataURL(file);
      return;
    }
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      try {
        const ratio = Math.min(1, maxDim / Math.max(img.width, img.height));
        const w = Math.round(img.width * ratio);
        const h = Math.round(img.height * ratio);
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, w, h);
        const dataUrl = canvas.toDataURL('image/jpeg', quality);
        URL.revokeObjectURL(url);
        resolve(dataUrl);
      } catch (err) {
        URL.revokeObjectURL(url);
        reject(err);
      }
    };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });
}

const ManageEvents = () => {
  const { locationId } = useParams();
  const navigate = useNavigate();
  const [location, setLocation] = useState(null);
  const [events, setEvents] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(blankEvent());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    locationsAPI.getById(locationId)
      .then((r) => setLocation(r.data))
      .catch(() => toast.error('Lokace nenalezena'));
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationId]);

  const refresh = async () => {
    try {
      const r = await eventsAPI.listByLocation(locationId);
      setEvents(r.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(blankEvent());
    setShowForm(true);
  };

  const openEdit = (e) => {
    setEditingId(e.id);
    setForm({
      name: e.name,
      start_date: e.start_date,
      end_date: e.end_date,
      max_persons: e.max_persons,
      price_per_person: e.price_per_person,
      description: e.description || '',
      details: e.details || '',
      photos: e.photos || [],
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.start_date || !form.end_date) {
      toast.error('Vyplňte název a termín akce');
      return;
    }
    if (form.start_date > form.end_date) {
      toast.error('Datum konce musí být po datu začátku');
      return;
    }
    try {
      setSaving(true);
      const payload = {
        location_id: locationId,
        name: form.name,
        start_date: form.start_date,
        end_date: form.end_date,
        max_persons: parseInt(form.max_persons) || 1,
        price_per_person: parseFloat(form.price_per_person) || 0,
        description: form.description || '',
        details: form.details || '',
        photos: form.photos || [],
      };
      if (editingId) {
        await eventsAPI.update(editingId, payload);
        toast.success('Akce upravena');
      } else {
        await eventsAPI.create(payload);
        toast.success('Akce přidána');
      }
      setShowForm(false);
      setEditingId(null);
      setForm(blankEvent());
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Chyba');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Smazat tuto akci?')) return;
    try {
      await eventsAPI.delete(id);
      toast.success('Akce smazána');
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Chyba');
    }
  };

  return (
    <div className="min-h-screen">
      <div
        className="relative bg-cover bg-center py-12 pb-24"
        style={{
          backgroundImage: `linear-gradient(rgba(0,0,0,0.65), rgba(0,0,0,0.65)), url('https://customer-assets.emergentagent.com/job_elementor-site-1/artifacts/ff4gwxo8_ChatGPT%20Image%2031.%203.%202026%2023_45_22.png')`,
        }}
      >
        <Header />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-32 sm:pt-44 lg:pt-52 pb-2 text-white">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="text-white hover:bg-white/10 mb-3 -ml-3"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Zpět na dashboard
          </Button>
          <h1 className="text-3xl sm:text-4xl font-bold mb-1">Organizované akce</h1>
          {location && (
            <p className="text-white/80">
              Lokalita: <Link to={`/location/${location.id}`} className="underline">{location.name}</Link>
            </p>
          )}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 -mt-12 pb-12 space-y-6 relative z-10">
        <div className="flex justify-end">
          {!showForm && (
            <Button
              onClick={openCreate}
              className="bg-orange-600 hover:bg-orange-700 shadow-lg"
              data-testid="add-event-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Přidat akci
            </Button>
          )}
        </div>

        {showForm && (
          <Card className="shadow-lg border-2 border-orange-200" data-testid="event-form">
            <CardHeader>
              <CardTitle className="text-lg">
                {editingId ? 'Upravit akci' : 'Nová organizovaná akce'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div>
                <Label htmlFor="ev-name">Název akce *</Label>
                <Input
                  id="ev-name"
                  placeholder="Např. Dětský letní tábor"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-1"
                  data-testid="event-name-input"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="ev-from">Od *</Label>
                  <Input
                    id="ev-from"
                    type="date"
                    value={form.start_date}
                    onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                    className="mt-1"
                    data-testid="event-start-input"
                  />
                </div>
                <div>
                  <Label htmlFor="ev-to">Do *</Label>
                  <Input
                    id="ev-to"
                    type="date"
                    value={form.end_date}
                    onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                    className="mt-1"
                    data-testid="event-end-input"
                  />
                </div>
                <div>
                  <Label htmlFor="ev-max">Max. počet osob *</Label>
                  <Input
                    id="ev-max"
                    type="number"
                    min="1"
                    value={form.max_persons}
                    onChange={(e) => setForm({ ...form, max_persons: e.target.value })}
                    className="mt-1"
                    data-testid="event-max-input"
                  />
                </div>
                <div>
                  <Label htmlFor="ev-price">Cena za osobu (Kč) *</Label>
                  <Input
                    id="ev-price"
                    type="number"
                    min="0"
                    value={form.price_per_person}
                    onChange={(e) => setForm({ ...form, price_per_person: e.target.value })}
                    className="mt-1"
                    data-testid="event-price-input"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="ev-desc">Krátký popis (pro výpis)</Label>
                <MarkdownHint />
                <Input
                  id="ev-desc"
                  placeholder="Týden plný dobrodružství v přírodě pro děti 8-14 let..."
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="ev-details">Detailní informace</Label>
                <MarkdownHint />
                <Textarea
                  id="ev-details"
                  rows={6}
                  placeholder="Program, vybavení, požadavky, **odkazy** např. https://moje-akce.cz, kontakt..."
                  value={form.details}
                  onChange={(e) => setForm({ ...form, details: e.target.value })}
                  className="mt-1"
                />
              </div>

              {/* Photos (max 2, auto-compressed) */}
              <div>
                <Label>Fotky akce <span className="text-gray-500 text-xs font-normal">(max 2 – automatická komprese)</span></Label>
                <div className="mt-2 flex flex-wrap gap-3">
                  {(form.photos || []).map((src, idx) => (
                    <div key={idx} className="relative w-32 h-24 rounded-lg overflow-hidden border" data-testid={`event-photo-${idx}`}>
                      <img src={src} alt={`Foto ${idx + 1}`} className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => setForm((f) => ({ ...f, photos: f.photos.filter((_, i) => i !== idx) }))}
                        className="absolute top-1 right-1 bg-red-600/90 hover:bg-red-700 text-white rounded-full p-1"
                        data-testid={`event-photo-remove-${idx}`}
                        aria-label="Odstranit fotku"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  {(form.photos?.length || 0) < 2 && (
                    <label
                      htmlFor="event-photo-upload"
                      className="w-32 h-24 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center cursor-pointer hover:border-orange-500 text-gray-500 text-xs text-center px-2"
                      data-testid="event-photo-upload-label"
                    >
                      + Přidat fotku
                    </label>
                  )}
                  <input
                    id="event-photo-upload"
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={async (e) => {
                      const files = Array.from(e.target.files || []);
                      const remaining = 2 - (form.photos?.length || 0);
                      if (remaining <= 0) {
                        toast.error('Maximálně 2 fotky');
                        e.target.value = '';
                        return;
                      }
                      const toAdd = files.slice(0, remaining);
                      try {
                        const compressed = await Promise.all(toAdd.map((f) => compressImage(f)));
                        setForm((f) => ({ ...f, photos: [...(f.photos || []), ...compressed] }));
                        toast.success(`Přidáno ${compressed.length} ${compressed.length === 1 ? 'foto' : 'foto(ek)'}`);
                      } catch (err) {
                        console.error(err);
                        toast.error('Nepodařilo se nahrát fotky');
                      } finally {
                        e.target.value = '';
                      }
                    }}
                  />
                </div>
              </div>
              <div className="flex gap-2 justify-end pt-2 border-t">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowForm(false);
                    setEditingId(null);
                  }}
                >
                  <X className="w-4 h-4 mr-1" /> Zrušit
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-orange-600 hover:bg-orange-700"
                  data-testid="event-save-btn"
                >
                  <Save className="w-4 h-4 mr-1" />
                  {saving ? 'Ukládám…' : 'Uložit'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {events.length === 0 && !showForm && (
          <Card>
            <CardContent className="p-12 text-center text-gray-500">
              <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="mb-1">Zatím žádné akce.</p>
              <p className="text-xs">Když přidáte akci, kalendář se v daném termínu zablokuje pro běžné rezervace.</p>
            </CardContent>
          </Card>
        )}

        {events.map((e) => (
          <Card key={e.id} data-testid={`event-${e.id}`}>
            <CardContent className="p-5">
              <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                <div>
                  <h3 className="font-bold text-xl">{e.name}</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    📅 {e.start_date} → {e.end_date}
                  </p>
                </div>
                <Badge className="bg-orange-600 text-white">
                  {e.price_per_person} Kč / osoba
                </Badge>
              </div>
              <div className="flex flex-wrap gap-4 text-sm text-gray-700 mb-3">
                <span className="flex items-center gap-1">
                  <Users className="w-3.5 h-3.5" /> max {e.max_persons} osob
                </span>
                <span className="flex items-center gap-1">
                  <CreditCard className="w-3.5 h-3.5" />
                  předpokládaný výnos {e.max_persons * e.price_per_person} Kč
                </span>
              </div>
              {e.description && <p className="text-sm text-gray-700 mb-3">{e.description}</p>}
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => openEdit(e)}>
                  <Edit3 className="w-3.5 h-3.5 mr-1" /> Upravit
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-red-600 border-red-300 hover:bg-red-50"
                  onClick={() => handleDelete(e.id)}
                  data-testid={`delete-event-${e.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5 mr-1" /> Smazat
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Footer />
    </div>
  );
};

export default ManageEvents;
